import { useCallback } from 'react';

import type { AuthConfig, AuthContext } from '@/auth/authUtils';
import { buildAuthUrl, clearAuthContext, finalizeUserSession, getAuthContext } from '@/auth/authUtils';
import type { UserSession } from '@/auth/sessionTypes';

type TokenExchangeResponse = {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
};

export type OidcAuthResult = {
  userSession: UserSession;
  state: string;
};

// OIDC-specific browser flow helpers. The public `useAuth` hook owns shared
// loading/error state, while this hook owns OIDC token exchange and persistence.
export const useOidcAuth = () => {
  const startOidcLogin = useCallback((authConfig: AuthConfig, email: string) => {
    window.location.href = buildAuthUrl(authConfig, email);
  }, []);

  const exchangeCodeForToken = useCallback(async (code: string, authContext: AuthContext) => {
    const response = await fetch('/api/control-plane/auth/token-exchange', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        authentication_id: authContext.authenticationId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange authorization code for token');
    }

    return (await response.json()) as TokenExchangeResponse;
  }, []);

  const handleOAuthCallback = useCallback(
    async (code: string, state: string): Promise<OidcAuthResult> => {
      try {
        const authContext = getAuthContext();
        if (!authContext) {
          throw new Error('Authentication context not found. Please try logging in again.');
        }

        const tokenData = await exchangeCodeForToken(code, authContext);
        const userSession: UserSession = {
          email: authContext.email,
          authentication_id: authContext.authenticationId,
          authenticated_at: new Date().toISOString(),
          auth_protocol: 'oidc',
          access_token: tokenData.access_token || tokenData.id_token,
          refresh_token: tokenData.refresh_token,
        };

        return { userSession: finalizeUserSession(userSession), state };
      } catch (err) {
        clearAuthContext();
        throw err;
      }
    },
    [exchangeCodeForToken],
  );

  return {
    handleOAuthCallback,
    startOidcLogin,
  };
};
